import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    Pipe,
    PipeTransform,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StateService } from '@uirouter/core';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, skipWhile, take, takeWhile, tap } from 'rxjs/operators';
import * as fromDataServices from '../../data-services';
import * as fromLookups from '../../lookups';
import { IStatusMessage, Process, ResponseStatus } from '../../shared';
import {
    EditableColumn,
    FileUpload,
    FilterService,
    SelectItem,
    Table
} from '../../shared/efaact-primeng.module';
import { IShowErrors } from '../../shared/errors-sidenav';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IUserInfo } from '../../user-info';
import { ITimeCard } from '../itime-card';
import { TimeCardDocumentation } from '../store/time-card-documentation.model';
@Component({
    selector: 'ef-time-card-documentation-presenter',
    templateUrl: './time-card-documentation-presenter.component.html',
    styleUrls: ['./time-card-documentation-presenter.component.scss'],
    standalone: false
})
export class TimeCardDocumentationPresenterComponent
    implements AfterViewInit, OnDestroy, OnChanges
{
    @ViewChild('dataTable', { static: true })
    protected table: Table;
    @ViewChild('timecardAttachmentUploader', { static: true })
    protected attachment: File;
    @ViewChild('docHours', { static: true })
    protected docHours: EditableColumn;
    @ViewChild('docHours', { static: true, read: Element })
    protected docHoursEl: Element;
    @ViewChildren(EditableColumn)
    private editableColumns: QueryList<EditableColumn>;
    public header = 'Documentation';
    private _process: Process;
    @Input() public set process(value: Process) {
        this._process = value;
    }
    public get process(): Process {
        return this._process;
    }
    @Input() userInfo: Partial<IUserInfo>;
    public timecardDoc: UntypedFormGroup;
    private changed: Array<TimeCardDocumentation> = [];
    private deleted: Array<TimeCardDocumentation> = [];
    timecardAttachmentUploader: FileUpload;
    @Output() public Save = new Subject<Array<TimeCardDocumentation>>();
    @Output() public Delete = new Subject<TimeCardDocumentation>();
    @Output() public DeleteAttachment = new Subject<string>();
    @Output() public Close = new Subject();
    @Output() public ShowError = new EventEmitter<IShowErrors>();
    @Output() public UploadAttachment = new Subject<{
        process: string;
        entityId: number;
        fileId: string;
        meta: File;
        file: string;
    }>();
    @Input() environment: any;
    public TimecardDoc: Array<TimeCardDocumentation>;

    // Getter to properly format accepted files for file upload
    public get acceptedFiles(): string {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent: acceptedFiles getter called',
            environment: this.environment,
            acceptedFiles: this.environment?.acceptedFiles
        });

        if (!this.environment?.acceptedFiles) {
            this.log.verbose({
                type: 'TimeCardDocumentationPresenterComponent: No acceptedFiles found in environment, returning empty string'
            });
            return '';
        }

        // Convert file extensions to MIME types for the accept attribute
        const mimeTypes = this.getMimeTypesForExtensions();
        const result = mimeTypes.join(',');
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent: acceptedFiles result',
            result
        });
        return result;
    }

    // Helper method to convert file extensions to MIME types
    private getMimeTypesForExtensions(): string[] {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent: getMimeTypesForExtensions called',
            acceptedFiles: this.environment?.acceptedFiles
        });

        if (!this.environment?.acceptedFiles) {
            this.log.verbose({
                type: 'TimeCardDocumentationPresenterComponent: No acceptedFiles found, returning empty array'
            });
            return [];
        }

        const extensionToMimeType: { [key: string]: string } = {
            // PDF
            '.pdf': 'application/pdf',

            // Image types
            '.jpg': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.gif': 'image/gif',
            '.png': 'image/png',
            '.webp': 'image/webp',
            '.svg': 'image/svg+xml',
            '.bmp': 'image/bmp',
            '.tiff': 'image/tiff',
            '.tif': 'image/tiff',
            '.ico': 'image/x-icon',
            '.jfif': 'image/jpeg',
            '.pjpeg': 'image/jpeg',
            '.pjp': 'image/jpeg',
            '.avif': 'image/avif',
            '.heic': 'image/heic',
            '.heif': 'image/heif',

            // Word document types
            '.doc': 'application/msword',
            '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            '.docm': 'application/vnd.ms-word.document.macroEnabled.12',
            '.dot': 'application/msword',
            '.dotx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
            '.dotm': 'application/vnd.ms-word.template.macroEnabled.12',
            '.rtf': 'application/rtf',
            '.odt': 'application/vnd.oasis.opendocument.text',

            // Excel file types
            '.xls': 'application/vnd.ms-excel',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
            '.xlsb': 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
            '.xlt': 'application/vnd.ms-excel',
            '.xltx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
            '.xltm': 'application/vnd.ms-excel.template.macroEnabled.12',
            '.ods': 'application/vnd.oasis.opendocument.spreadsheet',

            // CSV
            '.csv': 'text/csv'
        };

        const extensions = this.environment.acceptedFiles
            .split(',')
            .map(ext => ext.trim().toLowerCase())
            .filter(ext => ext.length > 0);

        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent: Parsed extensions',
            extensions
        });

        const mimeTypes = extensions
            .map(ext => extensionToMimeType[ext])
            .filter(mimeType => mimeType !== undefined);

        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent: MIME types found',
            mimeTypes
        });
        return mimeTypes;
    }

    // Helper method to get user-friendly allowed file types
    public getAllowedFileTypesDisplay(): string {
        if (!this.environment?.acceptedFiles) {
            return 'All file types';
        }

        const extensions = this.environment.acceptedFiles
            .split(',')
            .map(ext => ext.trim().toUpperCase().replace('.', ''))
            .filter(ext => ext.length > 0);

        // Group extensions by type for better display
        const imageTypes = extensions.filter(ext =>
            ['JPG', 'JPEG', 'GIF', 'PNG', 'WEBP', 'SVG', 'BMP', 'TIFF', 'TIF', 'ICO', 'JFIF', 'PJPEG', 'PJP', 'AVIF', 'HEIC', 'HEIF'].includes(ext)
        );
        const documentTypes = extensions.filter(ext =>
            ['PDF', 'DOC', 'DOCX', 'DOCM', 'DOT', 'DOTX', 'DOTM', 'RTF', 'ODT'].includes(ext)
        );
        const spreadsheetTypes = extensions.filter(ext =>
            ['XLS', 'XLSX', 'XLSM', 'XLSB', 'XLT', 'XLTX', 'XLTM', 'ODS', 'CSV'].includes(ext)
        );

        const groups = [];
        if (imageTypes.length > 0) {
            groups.push(`Images (${imageTypes.join(', ')})`);
        }
        if (documentTypes.length > 0) {
            groups.push(`Documents (${documentTypes.join(', ')})`);
        }
        if (spreadsheetTypes.length > 0) {
            groups.push(`Spreadsheets (${spreadsheetTypes.join(', ')})`);
        }

        return groups.join(' | ');
    }

    // Helper method to validate file type
    private isValidFileType(file: File): boolean {
        if (!this.environment?.acceptedFiles) {
            return true; // If no restrictions, allow all files
        }

        const acceptedExtensions = this.environment.acceptedFiles
            .split(',')
            .map(ext => ext.trim().toLowerCase());

        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        return acceptedExtensions.includes(fileExtension);
    }
    private TimeCardsSubject = new ReplaySubject<Array<ITimeCard>>(1);
    private _timeCards: Array<ITimeCard>;
    @Input() public set TimeCards(value: Array<ITimeCard>) {
        this._timeCards = value;
        this.TimeCardsSubject.next(value);
    }
    public get TimeCards(): Array<ITimeCard> {
        return this._timeCards;
    }
    ClassesForAuth = new ReplaySubject<SelectItem[]>(1);
    @Input() showDocumentation: boolean;
    @Input() Tasks: Array<fromLookups.ITask>;
    @Input() ServiceItems: Array<fromLookups.IServiceItem>;

    public Classes: Array<SelectItem>;
    private _classList: fromDataServices.TimecardClass.TimecardClass[];
    @Input() public set ClassList(
        value: fromDataServices.TimecardClass.TimecardClass[]
    ) {
        this._classList = value;
        this.Classes = value.map(x => ({
            label: x.ClassName,
            value: x.ClassListID
        }));
    }
    public get ClassList(): fromDataServices.TimecardClass.TimecardClass[] {
        return this._classList;
    }
    @Input() includeClassify: boolean;
    ValidatingSubject = new ReplaySubject<boolean>(1);
    private _validating: boolean;
    @Input() public set validating(value: boolean) {
        this._validating = value;
        this.ValidatingSubject.next(value);
    }
    public get validating(): boolean {
        return this._validating;
    }
    ApprovingSubject = new ReplaySubject<boolean>(1);
    private _approving: boolean;
    @Input() public set approving(value: boolean) {
        this._approving = value;
        this.ApprovingSubject.next(value);
    }
    public get approving(): boolean {
        return this._approving;
    }
    public documentationSubject = new ReplaySubject<
        Array<TimeCardDocumentation>
    >(1);
    private active = true;
    private _documentation: Array<TimeCardDocumentation>;
    @Input() public set documentation(value: Array<TimeCardDocumentation>) {
        if (value) {
            this._documentation = [...value];
            this.log.verbose({
                type: 'TimeCardDocumentationPresenterComponent:documentation:set',
                value
            });

            let docsWithSameTimeID = [];
            this.updatedDoc = null;
            if (this.newDoc != null) {
                this._documentation.forEach(d => {
                    if (d.EmpTimeID == this.newDoc.EmpTimeID) {
                        docsWithSameTimeID.push(d);
                    }
                });
                docsWithSameTimeID = docsWithSameTimeID.sort(
                    (a, b) => a.WorkID - b.WorkID
                );
                const i = docsWithSameTimeID.length - 1;
                if (i != -1) {
                    this.updatedDoc = docsWithSameTimeID[i];
                }
            }
            if (this.attachmentUpload == true && this.updatedDoc != null) {
                const fileReader = new FileReader();
                fileReader.onload = (e: any) => {
                    this.log.verbose({
                        type: 'TimeCardDocumentationPresenterComponent:onFileReaderLoad',
                        e
                    });
                    // Will print the base64 here.
                    const result = e.target.result as any;
                    if (this.updatedDoc != null) {
                        this.UploadAttachment.next({
                            process: 'timecard',
                            entityId: this.updatedDoc.WorkID,
                            fileId: null, // Guid.newGuid(),
                            meta: this.attachmentForUpload,
                            file: result.substr(result.indexOf(',') + 1)
                        });
                        // this.editData(this.updatedDoc, 'Hours');
                        this.updatedDoc = null;
                        this.attachmentUpload = false;
                    } else {
                        this.UploadAttachment.next({
                            process: 'timecard',
                            entityId: this.editing.WorkID,
                            fileId: null, // Guid.newGuid(),
                            meta: this.attachmentForUpload,
                            file: result.substr(result.indexOf(',') + 1)
                        });
                        this.attachmentUpload = false;
                    }
                };

                // readAsDataURL is base64 encoded
                fileReader.readAsDataURL(this.attachmentForUpload);
            }
            this.documentationSubject.next(this._documentation);
        }
    }
    public get documentation(): Array<TimeCardDocumentation> {
        return this._documentation;
    }
    @Input() AddDocumentation$: Observable<ITimeCard>;
    private _selectedError: IStatusMessage;
    @Input() public set selectedError(value: IStatusMessage) {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:selectedError:set',
            value
        });
        if (!value) {
            return;
        }
        if (this._selectedError && this._selectedError.MsgEntityID) {
            const oldRow = document.getElementById(
                `row-${this._selectedError.MsgEntityID.toString()}`
            );
            if (oldRow) {
                oldRow.classList.remove('highlight');
                oldRow.classList.remove('p-highlight');
                oldRow.classList.remove('highlight-warning');
                oldRow.classList.remove('highlight-danger');
            }
        }
        this._selectedError = value;
        if (!(value && value.MsgEntityID)) {
            return;
        }
        const styles: string[] = [];
        styles.push('p-highlight');
        styles.push('highlight');
        if (value.StatusCode === ResponseStatus.Warning) {
            styles.push('highlight-warning');
        }
        if (value.StatusCode === ResponseStatus.Failed) {
            styles.push('highlight-danger');
        }
        const rowId = `row-${value.MsgEntityID.toString()}`;
        const row = document.getElementById(rowId);
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:selectedError1',
            value,
            row
        });
        if (!row) {
            // virtual row?
            const idx =
                this.TimecardDoc &&
                this.TimecardDoc.findIndex(
                    doc =>
                        doc.EmpAuthID.toString() ===
                        value.MsgEntityID.toString()
                );
            this.table.scrollToVirtualIndex(idx);
            setTimeout(() => {
                this.highlightRow(rowId, ...styles);
            }, 0);
            return;
        }
        this.highlightRow(rowId, ...styles);
    }
    public get selectedError(): IStatusMessage {
        return this._selectedError;
    }
    newWorkId = -1;
    cloned: { [s: string]: TimeCardDocumentation } = {};
    editing?: TimeCardDocumentation = undefined;
    selected: TimeCardDocumentation[] = [];
    newDoc: any;
    updatedDoc: any;
    attachmentForUpload: any;
    attachmentUpload?: boolean = false;
    timeDocText?: string = '';
    updatedDocHours?: number;
    dateFilters: any;
    moment: any;
    constructor(
        private log: LoggingService,
        public ss: StateService,
        private filterService: FilterService
    ) {
        this.moment = moment;
        this.timecardDoc = new UntypedFormGroup({
            hours: new UntypedFormControl()
        });
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.showDocumentation) {
            if (this.editing) {
                //this.onRowEditCancel(this.editing);
            }
        }
    }
    ngOnDestroy() {
        this.active = false;
    }
    ngAfterViewInit() {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:ngAfterViewInit',
            validating: this.validating,
            approving: this.approving
        });
        this.setupSubscriptions();
    }
    setupSubscriptions() {
        combineLatest([
            this.TimeCardsSubject,
            this.documentationSubject,
            this.ValidatingSubject,
            this.ApprovingSubject
        ])
            .pipe(
                takeWhile(() => this.active),
                tap(([tcs, docs, validating, approving]) =>
                    this.log.verbose({
                        type: 'TimeCardDocumentationPresenterComponent:docs',
                        tcs,
                        docs
                    })
                ),
                filter(([tcs, docs]) => !!tcs && !!docs)
            )
            .subscribe(([tcs, docs, validating, approving]) => {
                this.TimecardDoc = [
                    ...docs
                        .sort((a, b) =>
                            a.TaskLongName.localeCompare(b.TaskLongName)
                        )
                        .map((x, i, a) => {
                            const val = {
                                ...x,
                                TaskLongNameDisplay: x.TaskLongName,
                                timeCard: tcs.find(
                                    t => t.EmpTimeID === x.EmpTimeID
                                ),
                                IsReadOnly:
                                    x.IsReadOnly || validating || approving
                                        ? true
                                        : this.userInfo.IsSuperUser
                                        ? false
                                        : validating || approving
                            };
                            return val;
                        })
                ];
            });
        this.AddDocumentation$.pipe(
            takeWhile(() => this.active),
            tap(tc =>
                this.log.verbose({
                    type: 'TimeCardDocumentationPresenterComponent:AddDocumentation$:tap',
                    tc,
                    docs: this.documentation
                })
            )
        ).subscribe((tc: ITimeCard) => {
            this.documentationSubject
                .pipe(
                    skipWhile(
                        docs =>
                            !tc ||
                            docs.findIndex(
                                doc => doc.EmpTimeID === tc.EmpTimeID
                            ) < 0
                    ),
                    take(1)
                )
                .subscribe(docs => {
                    this.editData(
                        docs.find(
                            doc => doc.EmpTimeID === tc.EmpTimeID
                        ) as TimeCardDocumentation
                    );
                });
        });
    }
    addNewDoc($event: MouseEvent, tc: ITimeCard | TimeCardDocumentation) {
        if ($event) {
            $event.preventDefault();
        }
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:addNewDoc',
            $event,
            tc,
            editableColumns: this.editableColumns
        });
        let readOnly = true;
        let hours = 0;
        let WorkID = 0;
        if ((tc as ITimeCard).EmployeeHours) {
            hours = (tc as ITimeCard).EmployeeHours;
        } else {
            hours = (tc as TimeCardDocumentation).EmpTimeIDHours;
            WorkID = (tc as TimeCardDocumentation).WorkID;
        }
        if (!(tc as ITimeCard).IsReadOnly) {
            readOnly = false;
        }

        if (!readOnly) {
            const idx = this.documentation.findIndex(
                x => x.EmpTimeID === tc.EmpTimeID
            );
            if (!$event && WorkID > 0) {
                this.log.verbose({
                    type: 'TimeCardDocumentationPresenterComponent:addNewDoc:existing',
                    tc,
                    idx
                });
                this.editData(this.documentation[idx], 'Hours');
            } else {
                let workDate;
                if ((tc as ITimeCard).WorkDate) {
                    workDate = (tc as ITimeCard).WorkDate;
                } else {
                    workDate = (tc as TimeCardDocumentation).EmployeeWorkDate;
                }
                this.newDoc = {
                    WorkID: this.newWorkId,
                    EmpAuthID: tc.EmpAuthID,
                    EmpTimeID: tc.EmpTimeID,
                    ServiceItem: tc.ServiceItem,
                    TaskLongName: tc.TaskLongName,
                    TimeDocText: '',
                    EmployeeWorkDate: new Date(workDate),
                    IsReadOnly: false,
                    EmpTimeIDHours: hours,
                    Hours:
                        hours -
                        this.documentation
                            .filter(x => x.EmpTimeID === tc.EmpTimeID)
                            .reduce((a, c) => a + (c.Hours ?? 0), 0),
                    Status: {
                        StatusCode: ResponseStatus.None,
                        Message: 'New'
                    }
                };
                this.documentation = [...this.documentation, this.newDoc];
                this.editData(this.newDoc);
            }
        } else {
            this.log.verbose({
                type: 'TimeCardDocumentationPresenterComponent:addNewDoc',
                readOnly
            });
        }
    }
    editData(doc: TimeCardDocumentation, field?: string) {
        if (doc.IsReadOnly == false) {
            this.table.cancelRowEdit(this.editing);
            this.onRowEditCancel(this.editing);
            this.onRowEditInit(doc);
            this.table.initRowEdit(doc);
            setTimeout(() => {
                document.getElementById('focusOnMe').click();
                document.getElementById('focusOnMe').focus();
            }, 0);
        }
    }
    updateHourValidation() {
        const controls = this.timecardDoc.controls;
        let max = this.editing?.EmpTimeIDHours ?? 0;
        if (this.documentation && this.documentation.length > 0) {
            max =
                this.editing?.EmpTimeIDHours ??
                0 -
                    this.documentation
                        .filter(
                            x =>
                                x.EmpTimeID === this.editing?.EmpTimeID &&
                                x.WorkID !== this.editing?.WorkID
                        )
                        .reduce((a, c) => a + (c.Hours ?? 0), 0);
        }
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:updateHourValidation',
            max
        });
        controls['hours'].setValidators([
            Validators.min(0),
            Validators.max(max),
            Validators.required
        ]);
    }
    onAttachmentSelect($event: { originalEvent: any; files: File[] }) {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onAttachmentSelect',
            $event
        });

        if (!$event.files || $event.files.length === 0) {
            return;
        }

        const file = $event.files[0];

        // Validate file type
        if (!this.isValidFileType(file)) {
            this.log.warn({
                type: 'TimeCardDocumentationPresenterComponent: Invalid file type',
                fileName: file.name,
                allowedTypes: this.getAllowedFileTypesDisplay()
            });
            // Clear the uploader
            if (this.timecardAttachmentUploader) {
                this.timecardAttachmentUploader.clear();
            }
            // Show error to user (you may need to implement error display)
            this.ShowError.emit({
                open: true,
                MsgEntityID: 0
            });
            return;
        }

        // Validate filename length
        if (file.name.length > 150) {
            this.log.warn({
                type: 'TimeCardDocumentationPresenterComponent: Filename too long',
                fileName: file.name,
                length: file.name.length
            });
            if (this.timecardAttachmentUploader) {
                this.timecardAttachmentUploader.clear();
            }
            this.ShowError.emit({
                open: true,
                MsgEntityID: 0
            });
            return;
        }

        // Check file size (10MB limit)
        if (file.size > 10485760) {
            this.log.warn({
                type: 'TimeCardDocumentationPresenterComponent: File too large',
                fileName: file.name,
                size: file.size
            });
            if (this.timecardAttachmentUploader) {
                this.timecardAttachmentUploader.clear();
            }
            this.ShowError.emit({
                open: true,
                MsgEntityID: 0
            });
            return;
        }

        // If we get here, the file is valid
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent: File validation passed',
            fileName: file.name,
            size: file.size
        });
    }

    // Handle upload errors
    onUploadError($event: any) {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onUploadError',
            $event
        });

        let errorMessage = 'Upload failed. Please try again.';

        if ($event && $event.xhr && $event.xhr.status) {
            switch ($event.xhr.status) {
                case 400:
                    errorMessage = 'Invalid file type or format.';
                    break;
                case 413:
                    errorMessage = 'File too large. Please select a smaller file.';
                    break;
                case 500:
                    errorMessage = 'Server error. Please try again later.';
                    break;
                default:
                    errorMessage = `Upload failed (${$event.xhr.status}). Please try again.`;
            }
        }

        this.ShowError.emit({
            open: true,
            MsgEntityID: 0
        });

        // Clear the uploader
        if (this.timecardAttachmentUploader) {
            this.timecardAttachmentUploader.clear();
        }
    }
    onAttachmentUpload($event: { files: File[] }) {
        if (this.editing.IsReadOnly) {
            return;
        }

        this.attachment = $event.files[0];
        this.attachmentForUpload = $event.files[0];
        if (this.editing?.WorkID == -1) {
            this.attachmentUpload = true;
            this.newDoc = {
                ...this.editing,
                timeCard: this.TimeCards.find(
                    x => x.EmpTimeID == this.editing?.EmpTimeID
                ),
                TimeDocText: this.timeDocText,
                Hours: this.updatedDocHours
            };
            this.onRowEditSave(this.newDoc);
            // this.onRowEditInit(this.newDoc);
        } else {
            this.attachmentUpload = false;
            const fileReader = new FileReader();
            fileReader.onload = (e: any) => {
                this.log.verbose({
                    type: 'TimeCardDocumentationPresenterComponent:onFileReaderLoad',
                    e
                });
                // Will print the base64 here.
                const result = e.target.result as any;

                this.UploadAttachment.next({
                    process: 'timecard',
                    entityId: this.editing.WorkID,
                    fileId: null, // Guid.newGuid(),
                    meta: this.attachment,
                    file: result.substr(result.indexOf(',') + 1)
                });
            };
            fileReader.readAsDataURL(this.attachment);
        }
    }
    onAttachmentDelete($event: any, fileId: string) {
        $event.preventDefault();
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onAttachmentDelete',
            fileId
        });
        this.DeleteAttachment.next(fileId);
    }
    clearDocForm() {
        const controls = this.timecardDoc.controls;
        controls['WorkID'].setValue(null);
        controls['hours'].setValue(null);
        controls['class'].setValue(null);
        controls['description'].setValue(null);
        controls['WorkID'].markAsPristine();
        controls['hours'].markAsPristine();
        controls['class'].markAsPristine();
        controls['description'].markAsPristine();
    }
    onRowDelete($event: any, doc: TimeCardDocumentation) {
        $event.preventDefault();
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onRowDelete',
            $event,
            doc
        });
        this.Delete.next(doc);
    }
    onRowEditInit(item: TimeCardDocumentation) {
        this.timeDocText = item.TimeDocText;
        this.updatedDocHours = item.Hours;
        this.cloned[item.id] = { ...item };
        const items = this.ClassList.filter(
            x => x.EmpAuthID === item.EmpAuthID
        );

        this.ClassesForAuth.next(
            items.map(x => ({
                label: x.ClassName,
                value: x.ClassListID
            }))
        );
        if (item.WorkID <= 0) {
            item.Hours =
                (item.EmpTimeIDHours ?? 0) -
                this.documentation
                    .filter(x => x.EmpTimeID === item.EmpTimeID && x.WorkID > 0)
                    .reduce((a, c) => a + (c.Hours ?? 0), 0);
            this.updatedDocHours = item.Hours;
        }
        this.editing = item;
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onRowEditInit',
            item,
            items,
            classList: this.ClassList,
            cloned: this.cloned,
            editing: this.editing
        });
        this.selected = [];
        this.updateHourValidation();

        setTimeout(() => {
            document.getElementById('focusOnMe')?.click();
            document.getElementById('focusOnMe')?.focus();
        }, 0);
    }

    onRowEditSave(item: TimeCardDocumentation) {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onRowEditSave',
            item,
            clonedDoc: this.cloned
        });
        if (this.timecardDoc.valid) {
            item.TimeDocText = this.timeDocText;
            item.Hours = this.updatedDocHours;
            this.Save.next([item]);
            this.timeDocText = '';
            this.updatedDocHours = undefined;
            delete this.cloned[item.id];
            delete this.editing;
        }
    }

    onRowEditCancel(item?: TimeCardDocumentation) {
        this.timeDocText = '';
        this.updatedDocHours = undefined;
        if (item && item.id) {
            this.documentation = this.documentation
                .filter(x => x.id !== item.id)
                .concat([this.cloned[item.id]]);

            delete this.cloned[item.id];
            delete this.editing;
        }
    }
    onStatusClick(item: TimeCardDocumentation) {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:onStatusClick',
            item
        });
        this.ShowError.emit({
            open: true,
            MsgEntityID: item.id.toString()
        });
    }
    highlightRow(rowId: string, ...styles: string[]) {
        const row = document.getElementById(rowId);
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:highlightRow',
            rowId,
            row,
            classes: styles
        });
        if (!row) {
            return;
        }
        row.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        row.classList.add(...styles);
    }
    customSort($event) {
        this.log.verbose({
            type: 'TimeCardDocumentationPresenterComponent:customSort',
            $event
        });
        this.documentation.sort((a, b) =>
            $event.order > 0
                ? (this.resolve($event.field, a) || '')
                      .toString()
                      .toLocaleLowerCase()
                      .localeCompare(
                          (this.resolve($event.field, b) || '')
                              .toString()
                              .toLocaleLowerCase()
                      )
                : (this.resolve($event.field, b) || '')
                      .toString()
                      .toLocaleLowerCase()
                      .localeCompare(
                          (this.resolve($event.field, a) || '')
                              .toString()
                              .toLocaleLowerCase()
                      )
        );
    }

    resolve(path, obj) {
        return path.split('.').reduce(function (prev, curr) {
            return prev ? prev[curr] : null;
        }, obj || self);
    }
}

@Pipe({
    name: 'filter',
    standalone: false
})
export class FilterPipe implements PipeTransform {
    transform(ary: any, field: string, id: string): any {
        try {
            const result =
                ary && ary.length
                    ? ary.filter(x => x[field].toString() === id.toString())
                    : { [`${field}`]: id };
            return result;
        } catch (e) {
            return undefined;
        }
    }
}

@Pipe({
    name: 'reducemax',
    standalone: false
})
export class ReduceMaxPipe implements PipeTransform {
    transform(ary: [], fn: any, max: number): any {
        try {
            const result = ary.reduce(eval(fn), 0) >= Number(max);
            return result;
        } catch (e) {
            return undefined;
        }
    }
}
@Pipe({
    name: 'reducenumber',
    standalone: false
})
export class ReduceNumberPipe implements PipeTransform {
    transform(ary: [], fn: any): any {
        try {
            return ary.reduce(eval(fn), 0);
        } catch (e) {
            return undefined;
        }
    }
}

@Pipe({
    name: 'classlist',
    standalone: false
})
export class ClassListPipe implements PipeTransform {
    transform(id: string, items: Array<SelectItem>): string {
        try {
            const c = items.find(x => x.value === id);
            return c ? c.label : '';
        } catch (e) {
            return undefined;
        }
    }
}
